import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSellerMilestones, useSellerRisks, useSellerRoomAccess } from '@/api/hooks';
import {
  ApiError,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorText,
  RiskSeverityChip,
  RiskStatusChip,
  formatDateOnly,
  todayYmd,
  visibleRisks,
  type RoomMilestone,
  type RoomRisk,
} from '@dataroom/shared';

/**
 * "Risks & timeline" (ADR-0047): the risks & issues and the timeline milestones
 * the deal team chose to share with the seller — the same entries the weekly
 * digest carries. Read-only: the deal team writes and closes entries; the AI
 * never adds or judges them.
 */
export function RisksPage() {
  const access = useSellerRoomAccess();
  if (access.status === 'login') return <Navigate to="/login" replace />;
  if (access.status === 'loading') {
    return (
      <p className="mx-auto w-full max-w-container px-4 py-6 md:px-6 text-sm text-muted-foreground">
        Loading…
      </p>
    );
  }
  if (access.status === 'forbidden') return <Navigate to="/" replace />;
  return <RisksPageInner roomId={access.roomId} />;
}

function RisksPageInner({ roomId }: { roomId: string }) {
  const risks = useSellerRisks(roomId);
  const milestones = useSellerMilestones(roomId);
  const [showResolved, setShowResolved] = useState(false);

  const unauthorized = [risks.error, milestones.error].some(
    (e) => e instanceof ApiError && e.status === 401,
  );
  if (unauthorized) return <Navigate to="/login" replace />;

  const all = risks.data ?? [];
  const shown = visibleRisks(all, showResolved);
  const resolvedCount = all.filter((r) => r.status === 'resolved').length;
  const dates = milestones.data ?? [];

  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Risks &amp; issues</CardTitle>
          <p className="text-sm text-muted-foreground">
            What the deal team is tracking that could affect the sale, shared with you. The deal
            team writes and updates these entries — the AI never adds or judges them. Questions
            about an entry? Ask the person who owns it.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {resolvedCount > 0 ? (
            <label className="flex w-fit items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
              />
              Show resolved ({resolvedCount})
            </label>
          ) : null}
          {risks.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : risks.error ? (
            <ErrorText error={risks.error} />
          ) : shown.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {all.length === 0
                ? 'Nothing has been shared with you.'
                : 'Nothing open — every entry shared with you is resolved.'}
            </p>
          ) : (
            <ul className="divide-y rounded-md border" aria-label="Risks and issues">
              {shown.map((risk) => (
                <SellerRiskRow key={risk.id} risk={risk} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Timeline</CardTitle>
          <p className="text-sm text-muted-foreground">
            Dates the deal team has planned and shared with you.
          </p>
        </CardHeader>
        <CardContent>
          {milestones.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : milestones.error ? (
            <ErrorText error={milestones.error} />
          ) : dates.length === 0 ? (
            <p className="text-sm text-muted-foreground">No dates have been shared with you.</p>
          ) : (
            <ol className="divide-y rounded-md border" aria-label="Timeline">
              {dates.map((m) => (
                <MilestoneRow key={m.id} milestone={m} />
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SellerRiskRow({ risk }: { risk: RoomRisk }) {
  const overdue = risk.status !== 'resolved' && risk.dueDate !== null && risk.dueDate < todayYmd();
  return (
    <li className="space-y-1.5 p-4" data-risk-id={risk.id}>
      <div className="flex flex-wrap items-center gap-2">
        <RiskSeverityChip severity={risk.severity} />
        <RiskStatusChip status={risk.status} />
        <span className="min-w-0 break-words text-sm font-semibold">{risk.title}</span>
      </div>
      {risk.description ? (
        <p className="whitespace-pre-wrap break-words text-sm text-ink-2">{risk.description}</p>
      ) : null}
      <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <div className="flex gap-1">
          <dt>Owner:</dt>
          <dd className="text-ink-2">{risk.owner ?? 'No owner yet'}</dd>
        </div>
        <div className="flex gap-1">
          <dt>Due:</dt>
          <dd className={overdue ? 'font-semibold text-danger' : 'text-ink-2'}>
            {risk.dueDate ? formatDateOnly(risk.dueDate) : 'No date'}
            {overdue ? ' (overdue)' : ''}
          </dd>
        </div>
      </dl>
    </li>
  );
}

function MilestoneRow({ milestone }: { milestone: RoomMilestone }) {
  const past = milestone.date < todayYmd();
  return (
    <li className="flex items-center justify-between gap-3 px-4 py-3">
      <span className={past ? 'text-sm text-muted-foreground' : 'text-sm font-medium'}>
        {milestone.name}
        {past ? <span className="sr-only"> (passed)</span> : null}
      </span>
      <span className="shrink-0 text-sm text-ink-2">{formatDateOnly(milestone.date)}</span>
    </li>
  );
}
